const DB_NAME = 'InterviewAudioDB';
const STORE_NAME = 'audioFiles';
const DB_VERSION = 1;

let db = null;

export const openDB = () => {
    return new Promise((resolve, reject) => {
        if (db) {
            resolve(db);
            return;
        }

        // Check if IndexedDB is supported
        if (!('indexedDB' in window)) {
            console.error("IndexedDB is not supported by this browser.");
            reject("IndexedDB not supported");
            return;
        }

        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = (event) => {
            console.error("IndexedDB error:", event.target.error);
            reject("IndexedDB error: " + event.target.error);
        };

        request.onsuccess = (event) => {
            db = event.target.result;
            console.log("IndexedDB opened successfully");
            resolve(db);
        };

        request.onupgradeneeded = (event) => {
            const dbInstance = event.target.result;
            if (!dbInstance.objectStoreNames.contains(STORE_NAME)) {
                // Use composite key: ['interviewId', 'nodeId']
                const store = dbInstance.createObjectStore(STORE_NAME, { keyPath: ['interviewId', 'nodeId'] });
                console.log("Object store created successfully");
                // Optionally, create indexes if needed for other queries
                // store.createIndex('interviewIdIndex', 'interviewId', { unique: false });
                // store.createIndex('nodeIdIndex', 'nodeId', { unique: false });
            }
        };
    });
};

export const addAudio = async (audioData) => {
    try {
        const dbInstance = await openDB();
        return new Promise((resolve, reject) => {
            // Ensure required fields are present
            if (!audioData || !audioData.interviewId || !audioData.nodeId || !audioData.file) {
                console.error("Invalid audio data format:", audioData);
                return reject("Invalid audio data format. Requires interviewId, nodeId, and file.");
            }
            // Ensure file is a Blob
             if (!(audioData.file instanceof Blob)) {
                console.error("File data is not a Blob:", audioData.file);
                return reject("File data must be a Blob.");
            }

            const transaction = dbInstance.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.put(audioData); // Use put to add or update

            request.onsuccess = () => {
                console.log("Audio data added/updated successfully:", { interviewId: audioData.interviewId, nodeId: audioData.nodeId });
                resolve();
            };

            request.onerror = (event) => {
                console.error("Error adding/updating audio data:", event.target.error);
                reject("Error adding/updating audio data: " + event.target.error);
            };
        });
    } catch (error) {
        console.error("Failed to add/update audio:", error);
        throw error; // Re-throw the error for upstream handling
    }
};

export const getAudio = async (interviewId, nodeId) => {
     try {
        const dbInstance = await openDB();
        return new Promise((resolve, reject) => {
            const transaction = dbInstance.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);
            // Use the composite key for retrieval
            const request = store.get([interviewId, nodeId]);

            request.onsuccess = (event) => {
                if (event.target.result) {
                    resolve(event.target.result.file); // Return only the file blob
                } else {
                    console.log(`Audio not found for interviewId: ${interviewId}, nodeId: ${nodeId}`);
                    resolve(null); // Indicate not found
                }
            };

            request.onerror = (event) => {
                console.error("Error getting audio data:", event.target.error);
                reject("Error getting audio data: " + event.target.error);
            };
        });
     } catch (error) {
         console.error("Failed to get audio:", error);
         throw error; // Re-throw the error
     }
};

// Optional: Function to clear the store or delete specific entries if needed
export const deleteAudio = async (interviewId, nodeId) => {
    try {
        const dbInstance = await openDB();
        return new Promise((resolve, reject) => {
            const transaction = dbInstance.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.delete([interviewId, nodeId]);

            request.onsuccess = () => {
                console.log(`Audio deleted successfully for interviewId: ${interviewId}, nodeId: ${nodeId}`);
                resolve();
            };

            request.onerror = (event) => {
                console.error("Error deleting audio data:", event.target.error);
                reject("Error deleting audio data: " + event.target.error);
            };
        });
    } catch (error) {
        console.error("Failed to delete audio:", error);
        throw error;
    }
};

// Call openDB on script load to initialize the database connection early
openDB().catch(error => console.error("Failed to initialize IndexedDB:", error)); 